import { Box, Flex, Text } from '@chakra-ui/react'

import IconClose from '@/components/icons/IconClose'
import Button from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'

import type { ReactNode } from 'react'

interface SelectionBarAction {
  label: string
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  disabledTooltip?: string
}

interface SelectionBarProps {
  selectedCount: number
  maxCount?: number
  primaryAction: SelectionBarAction
  secondaryAction: SelectionBarAction
  onClearSelection: () => void
}

export default function SelectionBar({
  selectedCount,
  maxCount,
  primaryAction,
  secondaryAction,
  onClearSelection
}: SelectionBarProps) {
  if (selectedCount === 0) {
    return null
  }

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      bg="bg.secondary"
      borderTop="1px solid"
      borderColor="border.DEFAULT"
      py={4}
      px={6}
      zIndex={100}
    >
      <Flex justify="space-between" align="center" mx="auto">
        <Text textStyle="body" fontWeight={500} color="text.primary">
          Selected: {selectedCount}
          {maxCount && selectedCount > maxCount ? ` (max ${maxCount})` : ''}
        </Text>

        <Flex gap={3} align="center">
          {primaryAction.disabledTooltip && primaryAction.disabled ? (
            <Tooltip
              content={primaryAction.disabledTooltip}
              positioning={{ placement: 'top' }}
            >
              <Button
                variant="primary"
                onClick={primaryAction.onClick}
                disabled={primaryAction.disabled}
              >
                {primaryAction.icon}
                {primaryAction.label}
              </Button>
            </Tooltip>
          ) : (
            <Button
              variant="primary"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
            >
              {primaryAction.icon}
              {primaryAction.label}
            </Button>
          )}

          <Button
            variant="secondary"
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled}
            bg="bg.surface"
            borderColor="border.DEFAULT"
            color="text.primary"
            _hover={{ bg: 'bg.tertiary' }}
          >
            {secondaryAction.icon}
            {secondaryAction.label}
          </Button>

          <IconClose
            boxSize={5}
            onClick={onClearSelection}
            color="text.secondary"
            cursor="pointer"
          />
        </Flex>
      </Flex>
    </Box>
  )
}
